import {
  CopyableText,
  DataGrid,
  formatDate,
  formatJson,
  Pre,
  StatusBadge,
  tryParse,
  type Severity
} from '@ijoin/components';
import { useGetAdminRecordKeeperExportQueueSuspense } from '@ijoin/hooks';
import { Paper, Stack, Tab, Tabs } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import { FC, SyntheticEvent, useState } from 'react';

import { SubmissionDiffSections } from './SubmissionDiffSections';

type ResponseDetailEntry = {
  message?: string;
  requestType?: string;
  status?: string;
  timeStamp?: string;
};

const RESPONSE_STATUS_SEVERITY: Record<string, Severity> = {
  error: 'error',
  fail: 'error',
  failure: 'error',
  pending: 'warning',
  success: 'success',
  warning: 'warning'
};

const formatResponseMessage = (raw: unknown): string => {
  if (raw == null) return '';
  if (typeof raw !== 'string') return JSON.stringify(raw, null, 2);
  const parsed = tryParse<unknown>(raw);
  if (parsed && typeof parsed === 'object') {
    return JSON.stringify(parsed, null, 2);
  }
  return raw;
};

const responseColumns: GridColDef<ResponseDetailEntry & { id: number }>[] = [
  {
    field: 'status',
    filterable: false,
    headerName: 'Status',
    renderCell: ({ value }) =>
      value ? (
        <StatusBadge
          severity={
            RESPONSE_STATUS_SEVERITY[String(value).toLowerCase()] ?? 'info'
          }>
          {value as string}
        </StatusBadge>
      ) : null,
    sortable: false,
    width: 120
  },
  {
    field: 'requestType',
    filterable: false,
    flex: 0.5,
    headerName: 'Type',
    minWidth: 140,
    sortable: false,
    type: 'string'
  },
  {
    field: 'message',
    filterable: false,
    flex: 1,
    headerName: 'Message',
    minWidth: 240,
    renderCell: ({ value }) => {
      const formatted = formatResponseMessage(value);
      return (
        <Stack
          alignItems='flex-start'
          direction='row'
          spacing={1}
          sx={{ py: 1, width: '100%' }}>
          <Pre sx={{ flex: 1 }}>{formatted}</Pre>
          {formatted && <CopyableText value={formatted} />}
        </Stack>
      );
    },
    sortable: false
  },
  {
    field: 'timeStamp',
    filterable: false,
    headerName: 'Timestamp',
    sortable: false,
    valueFormatter: value =>
      value
        ? formatDate(value as string, { format: 'MM/DD/YYYY h:mm:ss A' })
        : '',
    valueGetter: (_value, row) =>
      row.timeStamp ? new Date(row.timeStamp) : null,
    width: 180
  }
];

export const SubmissionDetailsTabs: FC<{
  recordKeeperExportQueueId: string;
}> = props => {
  const [tab, setTab] = useState('response');

  const details = useGetAdminRecordKeeperExportQueueSuspense(
    props.recordKeeperExportQueueId
  );

  const onTabChange = (_event: SyntheticEvent, value: string) => setTab(value);

  const parsedResponse = details.data.responseDetails
    ? tryParse<ResponseDetailEntry[]>(details.data.responseDetails, [])
    : [];
  const responseRows: (ResponseDetailEntry & { id: number })[] = Array.isArray(
    parsedResponse
  )
    ? parsedResponse.map((entry, i) => ({ ...entry, id: i }))
    : [];

  return (
    <Stack spacing={2}>
      <Tabs onChange={onTabChange} value={tab}>
        <Tab
          data-testid='tabResponseDetails'
          label='Response Details'
          value='response'
        />
        <Tab
          data-testid='tabSubmissionPayload'
          label='Submission Payload'
          value='payload'
        />
        <Tab data-testid='tabChanges' label='Changes' value='changes' />
      </Tabs>

      {tab === 'changes' && <SubmissionDiffSections details={details.data} />}

      {tab === 'payload' && (
        <Paper variant='outlined'>
          <Pre>
            {details.data.submissionPayload
              ? formatJson(details.data.submissionPayload)
              : 'No submission payload'}
          </Pre>
        </Paper>
      )}

      {tab === 'response' && (
        <DataGrid
          columns={responseColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          getRowId={row => row.id}
          hideFooter
          rows={responseRows}
          showColumnMenu
          slotProps={{
            noRowsOverlay: { message: 'No response details' },
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'submissionLogResponseDetail'
            }
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              alignItems: 'flex-start',
              py: 1
            }
          }}
        />
      )}
    </Stack>
  );
};

SubmissionDetailsTabs.displayName = 'SubmissionDetailsTabs';
